import { Users, Award, CheckCircle, ArrowRight, X } from "lucide-react";
import logoImg from "../assets/logoiii.png";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../redux/slices/usersSlice";
import { useState } from "react";
import { updatePlan } from "../redux/slices/plansSlice";
import { Toast } from "../components/Toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SubscriptionPlanCard = ({ 
  plan, 
  isActive = false, 
  userHasActivePlan = false, 
  subscriptionId = null 
}) => {
  if (!plan) return null;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    message: "",
    onConfirm: () => {},
  });
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const id = user?.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    setConfirmationDialog({
      open: true,
      message: `¿Deseas suscribirte al plan ${plan.name}? Se te redirigirá a nuestra landing page para realizar el pago con el administrador.`,
      onConfirm: async () => {
        try {
          setError(null);
          setIsLoading(true);

          // Abrir chat de Telegram en nueva pestaña
          window.open('https://grupokore.tiendup.com/', '_blank');

          // Mantener la lógica de creación de solicitud en el backend
          const result = await dispatch(
            updatePlan({
              userId: user.id,
              subscriptionPlanId: plan.id,
            })
          );

          if (result.payload) {
            // Mostrar toast informativo
            setToast({
              message: "✅ Solicitud recibida. Tu suscripción está pendiente de aprobación.",
              type: "success"
            });
            
            // Redirigir a home después de un breve retraso
            // para que el usuario pueda ver el mensaje de toast
            setTimeout(() => {
              navigate("/home");
            }, 1500);
          } else if (result.error) {
            throw new Error(result.error.message);
          }
        } catch (error) {
          console.error("Error en suscripción:", error);
          const errorMessage = error.message || "Ocurrió un error al procesar tu solicitud";
          setError(errorMessage);
          setToast({
            message: errorMessage,
            type: "error"
          });
        } finally {
          setIsLoading(false);
          setConfirmationDialog(prev => ({...prev, open: false}));
        }
      }
    });
  };

  // Función para manejar la cancelación de la suscripción
  const handleCancelSubscription = async () => {
    // Verificamos que tengamos un ID de suscripción válido
    if (!subscriptionId) {
      setToast({
        message: "Error: No se pudo identificar la suscripción a cancelar",
        type: "error"
      });
      setCancelDialog(false);
      return;
    }
    
    try {
      setCancelLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4005';

      const response = await axios.put(
        `${apiUrl}/api/v1/userSubscriptions/user-cancel/${subscriptionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );

      setCancelDialog(false);
      
      // Mostrar mensaje de éxito
      setToast({
        message: response.data.message || "Tu suscripción ha sido cancelada. Podrás seguir utilizando el servicio hasta el final del período contratado.",
        type: "success"
      });
      
      // Forzar recarga de la página después de un breve tiempo para actualizar el estado
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error("Error al cancelar la suscripción:", error);
      setToast({
        message: `Error: ${error.response?.data?.message || "No se pudo cancelar la suscripción. Intenta más tarde."}`,
        type: "error"
      });
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl lg:max-w-6xl mx-auto px-2 relative">
     
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {confirmationDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-600 relative"
            style={{
              boxShadow: "0 0 20px rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)"
            }}
          >
            <button
              onClick={() => setConfirmationDialog(prev => ({...prev, open: false}))}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-xl font-bold mb-4 text-yellow-400">Confirmar suscripción</h3>
            <p className="mb-6 text-gray-200">{confirmationDialog.message}</p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmationDialog(prev => ({...prev, open: false}))}
                className="px-4 py-2 border border-gray-500 text-gray-200 rounded-lg hover:bg-gray-700 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmationDialog.onConfirm}
                className="px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition font-medium"
                disabled={isLoading}
              >
                {isLoading ? "Procesando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para cancelar la suscripción */}
      {cancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-600 relative"
            style={{
              boxShadow: "0 0 20px rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)"
            }}
          >
            <button
              onClick={() => setCancelDialog(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-xl font-bold mb-4 text-red-400">Cancelar suscripción</h3>
            <p className="mb-6 text-gray-200">
              ¿Estás seguro que deseas cancelar tu suscripción? Podrás seguir utilizando el servicio hasta el final del período contratado.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setCancelDialog(false)}
                className="px-4 py-2 border border-gray-500 text-gray-200 rounded-lg hover:bg-gray-700 transition"
                disabled={cancelLoading}
              >
                No, mantener
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelando..." : "Sí, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`w-full flex flex-col md:flex-row overflow-hidden rounded-3xl text-white transform transition-transform duration-300 hover:scale-105 border border-white/20 relative ${isActive ? 'ring-4 ring-green-500 ring-opacity-70' : ''}`}
        style={{
          backgroundImage: "url('/images/background1.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {isActive && (
          <div className="absolute top-0 right-0 bg-green-500 text-black font-medium px-4 py-1 rounded-bl-lg z-20 flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" />
            <span>Plan Activo</span>
          </div>
        )}
        
      
        <div className="p-6 sm:p-8 lg:p-10 flex-1 flex flex-col justify-center z-10 ">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-yellow-400 font-bold">
              {plan.name} 
            </h2>
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16">
              <img
                src={logoImg}
                alt="Plan logo"
                className="opacity-80 w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="flex gap-3">
              <Users className="w-5 h-5 mt-1 flex-shrink-0 text-gray-300" />
              <p className="text-sm sm:text-base lg:text-lg text-gray-200">{plan.detail}</p>
            </div>

            <div className="flex gap-3">
              <Award className="w-5 h-5 mt-1 flex-shrink-0 text-gray-300" />
              <p className="text-sm sm:text-base lg:text-lg text-gray-200">
                Perfil de miembro con insignia {plan.badgeTier}
              </p>
            </div>
          </div>
        </div>

        {/* Troquel solo visible en md+ */}
        <div className="hidden md:flex flex-col justify-between items-center px-4 relative z-0">
          <div className="absolute -top-1 left-1/2 w-4 h-4 bg-black rounded-full -translate-x-1/2 z-20" />
          <div className="flex flex-col justify-center items-center h-full">
            {Array.from({ length: 30 }).map((_, i) => (
              <div key={i} className="w-2 h-2 rounded-full bg-black opacity-80 my-[2px]" />
            ))}
          </div>
          <div className="absolute -bottom-1 left-1/2 w-4 h-4 bg-black rounded-full -translate-x-1/2 z-20" />
        </div>

        {/* Sección derecha */}
        <div className="p-6 sm:p-8 lg:p-10 w-full md:w-64 lg:w-80 flex flex-col justify-center items-center z-10 bg-black/60 md:bg-transparent">
          <div className="text-center">
           <p 
  className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-500 mb-1"
  style={{ textShadow: '0 0 8px rgba(234, 179, 8, 0.4)' }}
>
  Precio:
</p>
            <div className="mb-5">
              <span className="text-xl sm:text-2xl font-medium">ARS</span>
              <span className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold">
                {" "}
                ${typeof plan.price === "number" ? plan.price.toLocaleString() : "0"}
              </span>
            </div>
            {isActive ? (
              <div className="flex flex-col items-center gap-2">
                <div className="bg-green-600/20 border border-green-500/30 rounded-full px-6 py-2 text-green-400 text-sm font-medium flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  <span>Plan actual</span>
                </div>
                {/* Solo mostramos el botón de cancelación si tenemos un ID de suscripción válido */}
                {subscriptionId && (
                  <button
                    onClick={() => setCancelDialog(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded-full text-sm transition-colors mt-2"
                  >
                    Cancelar suscripción
                  </button>
                )}
              </div>
            ) : userHasActivePlan ? (
              <button
                className="bg-gray-800 hover:bg-blue-600 text-white font-medium px-6 py-2 lg:px-8 lg:py-3 rounded-full text-sm sm:text-base flex items-center"
                onClick={handleSubscribe}
              >
                <span>Cambiar plan</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-medium px-6 py-2 lg:px-8 lg:py-3 rounded-full text-sm sm:text-base"
                onClick={handleSubscribe}
                disabled={isLoading}
              >
                {isLoading ? "Procesando..." : "Comprar plan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanCard;